import {
  ChangeDetectorRef,
  Component,
  inject,
  Input,
  OnChanges,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';

import {
  Ticket,
  TicketAggregate,
  TicketRecordType,
} from 'src/app/models/ticket/ticket.model';
import { getTicketType } from 'src/app/models/ticket/ticket.util';
import { NOT_AVAILABLE } from 'src/app/shared/constants/display';

@Component({
  selector: 'app-ticket-count-chart',
  templateUrl: './ticket-count-chart.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class TicketCountChartComponent implements OnChanges {
  @Input() tickets: Ticket[];

  @Input() isLoading: boolean;

  @Input() ticketAggregate: TicketAggregate | null = null;

  ticketTypeCount = signal<TicketAggregate>(null);

  filteredTickets = signal<Ticket[]>([]);

  nonProactiveAverageTimeToRepair = signal<string>(NOT_AVAILABLE);

  proactiveAverageTimeToRepair = signal<string>(NOT_AVAILABLE);

  nonProactiveAverageTimeToFirstTouch = signal<string>(NOT_AVAILABLE);

  proactiveAverageTimeToFirstTouch = signal<string>(NOT_AVAILABLE);

  private cdr = inject(ChangeDetectorRef);

  ngOnChanges(): void {
    this.filteredTickets.set(this.tickets);
    if (this.tickets.length) this.updateTicketCounts();
  }

  updateTicketCounts(): void {
    this.isLoading = true;
    this.cdr.markForCheck();
    this.ticketTypeCount.set(this.ticketAggregate);
    const proactiveTickets = this.filteredTickets().filter(
      (ticket) => getTicketType(ticket)[0] === TicketRecordType.Proactive,
    );
    const nonProactiveTickets = this.filteredTickets().filter((ticket) => {
      const ticketType = getTicketType(ticket)[0];
      return ticketType !== TicketRecordType.Proactive && ticketType != null;
    });

    const proactiveAverageTimeToFirstTouch =
      this.calculateAverageTimeToFirstTouch(proactiveTickets);
    this.proactiveAverageTimeToFirstTouch.set(
      proactiveAverageTimeToFirstTouch !== null
        ? this.convertMillisecondsToDaysAndHours(
            proactiveAverageTimeToFirstTouch,
          )
        : NOT_AVAILABLE,
    );

    const nonProactiveAverageTimeToFirstTouch =
      this.calculateAverageTimeToFirstTouch(nonProactiveTickets);
    this.nonProactiveAverageTimeToFirstTouch.set(
      nonProactiveAverageTimeToFirstTouch !== null
        ? this.convertMillisecondsToDaysAndHours(
            nonProactiveAverageTimeToFirstTouch,
          )
        : NOT_AVAILABLE,
    );

    const proactiveAverageTimeToRepair =
      this.calculateAverageTimeToRepair(proactiveTickets);
    this.proactiveAverageTimeToRepair.set(proactiveAverageTimeToRepair);
    const nonProactiveAverageTimeToRepair =
      this.calculateAverageTimeToRepair(nonProactiveTickets);
    this.nonProactiveAverageTimeToRepair.set(nonProactiveAverageTimeToRepair);
    this.isLoading = false;
    this.cdr.markForCheck();
  }

  calculateAverageTimeToRepair(tickets: Ticket[]): string {
    let totalTime = 0;
    let count = 0;

    tickets.forEach((ticket) => {
      const type = getTicketType(ticket);
      if (
        type !== null &&
        type !== undefined &&
        ticket?.Time_to_Repair_Days__c !== null &&
        ticket?.Time_to_Repair_Days__c !== undefined
      ) {
        totalTime += ticket.Time_to_Repair_Days__c;
        count += 1;
      }
    });

    if (count === 0) {
      return NOT_AVAILABLE;
    }

    const averageTime = totalTime / count;
    return this.convertDaysToDaysAndHours(averageTime);
  }

  convertDaysToDaysAndHours(days: number): string {
    const wholeDays = Math.floor(days);
    const remainingHours = (days - wholeDays) * 24;
    const hours = Math.floor(remainingHours);
    const minutes = Math.floor((remainingHours - hours) * 60);

    if (wholeDays === 0) return `${hours} hr, ${minutes} min`;
    if (hours === 0) return `${wholeDays} days`;
    return `${wholeDays} days, ${hours} hr, ${minutes} min`;
  }

  convertMillisecondsToDaysAndHours(milliseconds: number): string {
    const days = Math.floor(milliseconds / (1000 * 60 * 60 * 24));
    const hours = Math.floor(
      (milliseconds % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60),
    );
    const minutes = Math.floor((milliseconds % (1000 * 60 * 60)) / (1000 * 60));
    return `${days} days, ${hours} hr, ${minutes} min`;
  }

  calculateAverageTimeToFirstTouch(tickets: Ticket[]): number | null {
    let totalTime = 0;
    let count = 0;

    tickets.forEach((ticket) => {
      if (ticket.First_touch__c !== null) {
        const createTime = new Date(ticket.CreatedDate).getTime();
        const firstTouchTime = new Date(ticket.First_touch__c).getTime();
        const timeDiff = firstTouchTime - createTime;
        totalTime += timeDiff;
        count += 1;
      }
    });

    if (count === 0) return null;
    return totalTime / count;
  }

  getTypeSummaryLabel(
    type: string,
    count: number,
    averagePickupTime: string,
    averageRepairTime: string,
  ): string {
    const ticketLabel = count === 1 ? 'ticket' : 'tickets';
    return `${type}: ${count} ${ticketLabel}. Average pickup time: ${averagePickupTime || NOT_AVAILABLE}. Average repair time: ${averageRepairTime || NOT_AVAILABLE}.`;
  }
}
